import { notificationCentreApi, type Notice, type NoticeGroup } from "../../api/notificationCentreApi";
import { offerUndo } from "../../lib/undo";

// O-R67 Undo for the notification centre (release 1.5.0 polish). "Mark read", "Mark all read" and "Snooze" change only
// the person's own inbox, so each offers "Undo" for about 8 seconds: mark the same notices unread again, or wake the
// snoozed notice (and mark it read again if it had been read before it was snoozed). Opening a notice also marks it
// read, but the person has left for the page then, so nothing is offered.

/** The ids of the unread, not snoozed notices a list shows (what "Mark all read" is about to change). */
export function unreadIds(groups: readonly NoticeGroup[] | undefined): number[] {
  return (groups ?? []).flatMap((g) => g.notices).filter((n) => !n.isRead && !n.snoozedUntilUtc).map((n) => n.id);
}

export function offerMarkReadUndo(notice: Notice, onChanged: () => void): number | null {
  return offerUndo({
    action: "mark-notice-read",
    done: "Marked read.",
    undone: "Back to unread.",
    undo: () => notificationCentreApi.markUnread([notice.id]),
    onUndone: onChanged
  });
}

export function offerMarkAllReadUndo(ids: number[], changed: number, onChanged: () => void): number | null {
  if (ids.length === 0 || changed === 0) return null;
  return offerUndo({
    action: "mark-all-notices-read",
    done: `Marked ${changed} notice${changed === 1 ? "" : "s"} read.`,
    undone: "Back to unread.",
    undo: () => notificationCentreApi.markUnread(ids),
    onUndone: onChanged
  });
}

export function offerSnoozeUndo(notice: Notice, message: string, onChanged: () => void): number | null {
  const wasRead = notice.isRead;
  return offerUndo({
    action: "snooze-notice",
    done: message,
    undone: "Back in your list.",
    undo: async () => {
      await notificationCentreApi.snooze([notice.id], "wake");
      if (wasRead) await notificationCentreApi.markRead([notice.id]);
    },
    onUndone: onChanged
  });
}
